import { create } from 'zustand';
import projectsService, { 
  Project, 
  CreateProjectDto, 
  UpdateProjectDto, 
  PaginationParams,
  PaginationMeta 
} from '../services/projects.service';
import { toast } from 'react-hot-toast';

interface ProjectsState {
  projects: Project[];
  selectedProject: Project | null;
  isLoading: boolean;
  error: string | null;
  pagination: PaginationMeta | null;
  lastModified: number;

  // Actions
  fetchProjects: (params?: PaginationParams) => Promise<void>;
  fetchProject: (id: number) => Promise<void>;
  createProject: (data: CreateProjectDto) => Promise<void>;
  updateProject: (id: number, data: UpdateProjectDto) => Promise<void>;
  deleteProject: (id: number) => Promise<void>;
  duplicateProject: (id: number) => Promise<void>;
  setSelectedProject: (project: Project | null) => void;
  clearError: () => void;
}

const useProjectsStore = create<ProjectsState>((set, get) => ({
  projects: [],
  selectedProject: null,
  isLoading: false,
  error: null,
  pagination: null,
  lastModified: 0,

  fetchProjects: async (params?: PaginationParams) => {
    set({ isLoading: true, error: null });
    try {
      const response = await projectsService.getProjects(params);
      
      if (response.status === 'success' && response.data) {
        set({ 
          projects: response.data, 
          pagination: response.pagination,
          isLoading: false 
        });
      } else {
        throw new Error(response.message || 'Failed to fetch projects');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch projects';
      set({ error: errorMessage, isLoading: false });
      toast.error(errorMessage);
    }
  },

  fetchProject: async (id: number) => {
    set({ isLoading: true, error: null });
    try {
      const response = await projectsService.getProject(id);
      
      if (response.status === 'success' && response.data) {
        set({ 
          selectedProject: response.data, 
          isLoading: false 
        });
      } else {
        throw new Error(response.message || 'Failed to fetch project');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch project';
      set({ error: errorMessage, isLoading: false });
      toast.error(errorMessage);
    }
  },

  createProject: async (data: CreateProjectDto) => {
    set({ isLoading: true, error: null });
    try {
      const response = await projectsService.createProject(data);
      
      if (response.status === 'success' && response.data) {
        const { projects } = get();
        set({ 
          projects: [response.data, ...projects],
          isLoading: false,
          lastModified: Date.now()
        });
        toast.success('Project created successfully');
      } else {
        throw new Error(response.message || 'Failed to create project');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create project';
      set({ error: errorMessage, isLoading: false });
      toast.error(errorMessage);
      throw error;
    }
  },

  updateProject: async (id: number, data: UpdateProjectDto) => {
    set({ isLoading: true, error: null });
    try {
      const response = await projectsService.updateProject(id, data);
      
      if (response.status === 'success' && response.data) {
        const { projects } = get();
        const updatedProjects = projects.map(p => 
          p.id === id ? response.data! : p
        );
        set({ 
          projects: updatedProjects,
          selectedProject: response.data,
          isLoading: false,
          lastModified: Date.now()
        });
        toast.success('Project updated successfully');
      } else {
        throw new Error(response.message || 'Failed to update project');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update project';
      set({ error: errorMessage, isLoading: false });
      toast.error(errorMessage);
      throw error;
    }
  },

  deleteProject: async (id: number) => {
    set({ isLoading: true, error: null });
    try {
      const response = await projectsService.deleteProject(id);
      
      if (response.status === 'success') {
        const { projects } = get();
        const filteredProjects = projects.filter(p => p.id !== id);
        set({ 
          projects: filteredProjects,
          isLoading: false,
          lastModified: Date.now()
        });
        toast.success('Project deleted successfully');
      } else {
        throw new Error(response.message || 'Failed to delete project');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete project';
      set({ error: errorMessage, isLoading: false });
      toast.error(errorMessage);
      throw error;
    }
  },

  duplicateProject: async (id: number) => {
    set({ isLoading: true, error: null });
    try {
      const response = await projectsService.duplicateProject(id);

      if (response.status === 'success' && response.data) {
        const { pagination } = get();
        await get().fetchProjects({ page: pagination?.page || 1, limit: pagination?.limit || 10 });
        set({ isLoading: false, lastModified: Date.now() });
        toast.success('Project duplicated successfully');
      } else {
        throw new Error(response.message || 'Failed to duplicate project');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to duplicate project';
      set({ error: errorMessage, isLoading: false });
      toast.error(errorMessage);
      throw error;
    }
  },

  setSelectedProject: (project: Project | null) => {
    set({ selectedProject: project });
  },

  clearError: () => {
    set({ error: null });
  }
}));

export default useProjectsStore;